import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';
import FaceCoverPanel from '../../components/FaceCoverPanel';
import { MODELS as REAL, FACE_AWARE, FACE_ON_SET, MODEL_SETTINGS, PPE_ITEMS, nonDefault }
  from '../../utils/aiModels';

// ── AI Modules: per-camera enable/disable of detection models. ──
// Enabling a model = adding its name to the camera's `detectors` list, saved via
// PUT /cameras/{idx} { detectors:[...] }. This is configuration (which models run),
// NOT a change to detection logic. Takes effect when the camera worker reloads.
//
// The catalogue itself (which models exist, their tunables and defaults) lives in
// utils/aiModels.js, because Guided Setup's models step writes the SAME keys —
// site-wide rather than per camera. One copy, so a corrected default cannot be
// right on one screen and wrong on the other.
//
// Non-face modules are shown in the grid. The face-identity detectors (unknown
// access control + known logging + banned watchlist + after-hours) are all driven
// by the single "Face recognition" switch above the grid, so they're not rows.

// The weights a module is actually running. Worth showing: yolofire.pt sat in
// production alerting on street dogs while missing real fires, and nothing on this
// page ever named it — so nobody thought to question it. `full` adds class names,
// parameter count and size for the tuning card; the module list stays to one line.
function ModelProps({ info, full }) {
  if (!info || !info.filename) return null;
  const mono = { fontFamily: 'var(--ind-mono)', fontSize: 11 };
  if (!info.exists) {
    return <span style={{ ...mono, color: 'var(--ind-bad, #d33)' }}>
      ⚠ {info.filename} MISSING — this module will not run · run download_models.py
    </span>;
  }
  if (info.failed) {
    return <span style={{ ...mono, color: 'var(--ind-bad, #d33)' }}>
      ⚠ {info.filename} failed to load — this module is not running
    </span>;
  }
  const bits = [info.filename];
  if (full) {
    if (info.size_mb)  bits.push(`${info.size_mb}MB`);
    if (info.params_m) bits.push(`${info.params_m}M params`);
    if (info.classes)  bits.push(info.classes.join('/'));
    bits.push(info.loaded ? 'loaded' : 'not yet loaded');
  }
  return <span style={{ ...mono, color: 'var(--ind-tx3)' }}>{bits.join(' · ')}</span>;
}

export default function IndustrialModulesPage() {
  const [cameras, setCameras] = useState([]);
  const [scope, setScope]     = useState(null);
  const [enabled, setEnabled] = useState([]);   // detectors for scope
  const [ppe, setPpe]         = useState(['helmet']);  // PPE items enforced (detector_config.helmet.ppe_items)
  const [tuning, setTuning]   = useState({});          // { model: { conf, persist, ... } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [msg, setMsg]         = useState(null);
  // Which weights each detector actually runs (GET /ui/detector-models), keyed by
  // model key. Class names + params are only present once a worker has loaded the
  // model — the endpoint never loads one just to answer.
  const [models, setModels]   = useState({});

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const d = await apiFetch('/ui/cameras').then(r => r.json());
        if (!live) return;
        const cams = d.cameras || [];
        setCameras(cams);
        if (cams.length) select(cams[0].idx, cams);
      } catch {
        if (live) setMsg({ type: 'error', text: 'Failed to load cameras' });
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Model inventory is independent of camera scope — fetch once. A failure here
  // must not block the page: the properties line simply doesn't render.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const d = await apiFetch('/ui/detector-models').then(r => r.json());
        if (!live) return;
        setModels(Object.fromEntries((d.models || []).map(m => [m.key, m])));
      } catch { /* properties line is optional */ }
    })();
    return () => { live = false; };
  }, []);

  const select = useCallback((idx, camList) => {
    const cams = camList || cameras;
    const cam = cams.find(c => c.idx === idx);
    setScope(idx);
    setEnabled(Array.isArray(cam?.detectors) ? [...cam.detectors] : []);
    const dc = cam?.detector_config || {};
    const items = dc.helmet?.ppe_items;
    setPpe(Array.isArray(items) && items.length ? [...items] : ['helmet']);
    // Load per-model tunables (fall back to defaults).
    const t = {};
    Object.keys(MODEL_SETTINGS).forEach(k => {
      const c = dc[k] || {};
      t[k] = {};
      MODEL_SETTINGS[k].forEach(s => { t[k][s.key] = c[s.key] != null ? c[s.key] : s.def; });
    });
    setTuning(t);
    setDirty(false);
    setMsg(null);
  }, [cameras]);

  const toggle = (key) => {
    setEnabled(e => e.includes(key) ? e.filter(k => k !== key) : [...e, key]);
    setDirty(true);
  };

  // Per-camera Face recognition master switch. OFF = person-only presence alerts
  // (no face ID, no known/banned matching) via the 'instant' detector — lighter +
  // privacy. ON = restore unknown-person face alerting.
  const faceOn = enabled.some(k => FACE_AWARE.includes(k));
  const setFace = (on) => {
    setEnabled(e => on
      ? [...new Set([...e.filter(k => k !== 'instant'), ...FACE_ON_SET])]
      : [...new Set([...e.filter(k => !FACE_AWARE.includes(k)), 'instant'])]);
    setDirty(true);
  };

  const togglePpe = (item) => {
    setPpe(p => p.includes(item) ? p.filter(x => x !== item) : [...p, item]);
    setDirty(true);
  };

  const setTune = (model, key, val) => {
    setTuning(t => ({ ...t, [model]: { ...(t[model] || {}), [key]: val } }));
    setDirty(true);
  };

  const save = async () => {
    if (scope == null) return;
    setSaving(true); setMsg(null);
    try {
      const body = { detectors: enabled };
      // Persist per-model tunables + PPE items for enabled models — but ONLY the
      // values that differ from the detector's own default (nonDefault). Writing
      // every slider at its displayed value is what pinned fire's require_growth
      // to `true` on any camera whose fire settings were ever saved, when the
      // detector defaults it to False: a screen's idea of a default became a
      // stored override nobody chose. A value set back to the default is sent as
      // null, which the PUT /cameras merge pops.
      const cam = cameras.find(c => c.idx === scope);
      const dc = {};
      enabled.forEach(k => {
        if (!MODEL_SETTINGS[k] || !tuning[k]) return;
        const stored = (cam?.detector_config || {})[k];
        const diff   = nonDefault(k, tuning[k], stored);
        if (Object.keys(diff).length) dc[k] = diff;
      });
      if (enabled.includes('helmet')) {
        const items  = ppe.length ? ppe : ['helmet'];
        const stored = ((cam?.detector_config || {}).helmet || {}).ppe_items;
        const isDef  = items.length === 1 && items[0] === 'helmet';
        if (!isDef || stored) {
          dc.helmet = { ...(dc.helmet || {}), ppe_items: isDef && !stored ? null : items };
        }
      }
      if (Object.keys(dc).length) body.detector_config = dc;
      const r = await apiFetch(`/cameras/${scope}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      setCameras(cs => cs.map(c => c.idx === scope
        ? { ...c, detectors: [...enabled], detector_config: { ...(c.detector_config || {}), ...dc } }
        : c));
      setDirty(false);
      setMsg({ type: 'success', text: 'Saved — camera is reloading to apply the new models.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const camName = cameras.find(c => c.idx === scope)?.name || (scope != null ? `Camera ${scope}` : '');

  return (
    <IndustrialLayout
      eyebrow="Detection engine"
      title="AI Models"
      subtitle={loading ? 'LOADING…' : `${cameras.length} CAMERAS · ${enabled.length} MODELS ON ${camName.toUpperCase()}`}
      banner={
        <div className="ind-banner info">
          Enable / disable which AI models run <b>per camera</b>. This is configuration only — detection logic is unchanged.
          Saving reloads that camera automatically so the change applies right away.
        </div>
      }>
      {loading ? <div className="ind-empty">Loading…</div> : (
        <>
          {/* camera selector */}
          <div className="ind-card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className="ind-eye" style={{ margin: 0 }}>Camera</span>
            <select value={scope ?? ''} onChange={e => { if (dirty && !window.confirm('Discard unsaved changes?')) return; select(Number(e.target.value)); }}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 13 }}>
              {cameras.map(c => <option key={c.idx} value={c.idx}>🎥 {c.name || `Camera ${c.idx}`}</option>)}
            </select>
            <button className="ind-btn" style={{ marginLeft: 'auto', background: 'var(--ind-accent)', color: '#fff', borderColor: 'var(--ind-accent)', fontWeight: 700 }}
              onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save models'}</button>
            {dirty && <span style={{ fontSize: 12, color: 'var(--ind-high)' }}>Unsaved</span>}
            {msg && <span style={{ fontSize: 12, color: msg.type === 'error' ? 'var(--ind-crit)' : 'var(--ind-ok)' }}>{msg.text}</span>}
          </div>

          {/* Face recognition master switch (per camera) — covers unknown access control
              + known logging + (optional) banned watchlist + after-hours in one place */}
          <div className="ind-card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <b style={{ fontSize: 14 }}>🧑 Face recognition (access control)</b>
              <span style={{ fontSize: 12, color: 'var(--ind-tx3)', maxWidth: 620 }}>
                {faceOn
                  ? 'ON — alerts on unauthorised (unknown) people + banned watchlist; enrolled/allowed people are whitelisted and logged for attendance.'
                  : 'OFF — person-only presence alerts: any person alerts, no face ID (lighter + privacy).'}
              </span>
            </div>
            <button className="ind-btn" style={{ marginLeft: 'auto', fontWeight: 700,
              background: faceOn ? 'var(--ind-ok)' : 'var(--ind-ink)', color: faceOn ? '#fff' : 'var(--ind-tx2)',
              borderColor: faceOn ? 'var(--ind-ok)' : 'var(--ind-line)' }}
              onClick={() => setFace(!faceOn)}>
              {faceOn ? '✓ Recognition ON' : 'Person-only (OFF)'}
            </button>
          </div>

          <div className="ind-rail">Other models on {camName}</div>
          <div className="ind-mods">
            {REAL.map(m => {
              const on = enabled.includes(m.key);
              return (
                <div key={m.key} className="ind-mod">
                  <div className="ic">{m.icon}</div>
                  <div className="n">
                    <b>{m.label}</b>
                    <span>{on ? 'enabled · ' : 'off · '}{m.desc}</span>
                    {models[m.key] && <span><ModelProps info={models[m.key]} /></span>}
                  </div>
                  <span className="ind-cost">{m.tier}</span>
                  <span className="ind-sw" role="switch" aria-checked={on} onClick={() => toggle(m.key)} style={{ cursor: 'pointer' }} />
                </div>
              );
            })}
          </div>

          {/* Shared backbone. person/pose aren't toggleable modules, but every module
              above depends on them, so a missing or failed one explains a whole page
              of silent detectors. */}
          {(models.person || models.pose) && (
            <div className="ind-card" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', padding: '8px 12px' }}>
              <span style={{ fontFamily: 'var(--ind-mono)', fontSize: 11, color: 'var(--ind-tx3)' }}>SHARED</span>
              {['person', 'pose'].filter(kk => models[kk]).map(kk => (
                <span key={kk} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--ind-tx2)' }}>{kk}</span>
                  <ModelProps info={models[kk]} full />
                </span>
              ))}
            </div>
          )}

          {/* PPE item selection — only when the PPE model is enabled */}
          {enabled.includes('helmet') && (
            <>
              <div className="ind-rail">PPE items to enforce</div>
              <div className="ind-card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {PPE_ITEMS.map(it => {
                  const on = ppe.includes(it.key);
                  return (
                    <button key={it.key} onClick={() => togglePpe(it.key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                        border: '1px solid ' + (on ? 'var(--ind-accent)' : 'var(--ind-line)'),
                        background: on ? 'var(--ind-accent)' : 'var(--ind-ink)', color: on ? '#fff' : 'var(--ind-tx2)' }}>
                      {it.icon} {it.label}{on ? ' ✓' : ''}{it.proven ? '' : ' *'}
                    </button>
                  );
                })}
                <span style={{ marginLeft: 'auto', fontFamily: 'var(--ind-mono)', fontSize: 11, color: 'var(--ind-tx3)' }}>
                  a body part with no gear on it fires a PPE alert · * not yet validated on site footage
                </span>
              </div>
            </>
          )}

          {/* per-model sensitivity / persistence */}
          {enabled.some(k => MODEL_SETTINGS[k]) && (
            <>
              <div className="ind-rail">Sensitivity &amp; persistence</div>
              {enabled.filter(k => MODEL_SETTINGS[k]).map(k => {
                const m = REAL.find(x => x.key === k);
                return (
                  <div key={k} className="ind-card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 18 }}>{m?.icon}</span><b>{m?.label}</b>
                      <span style={{ marginLeft: 'auto' }}><ModelProps info={models[k]} full /></span>
                    </div>
                    {MODEL_SETTINGS[k].map(s => {
                      const val = tuning[k]?.[s.key] ?? s.def;
                      return (
                      <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <label style={{ fontSize: 12, color: 'var(--ind-tx2)', width: 150 }}>{s.label}</label>
                        {s.type === 'select' ? (
                          <>
                            <select value={val} onChange={e => setTune(k, s.key, e.target.value)}
                              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)' }}>
                              {(s.options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            {s.hint && <span style={{ fontSize: 10, color: 'var(--ind-tx3)' }}>{s.hint}</span>}
                          </>
                        ) : s.type === 'bool' ? (
                          <>
                            <input type="checkbox" checked={!!val}
                              onChange={e => setTune(k, s.key, e.target.checked)}
                              style={{ width: 18, height: 18, accentColor: 'var(--ind-accent)' }} />
                            <span style={{ fontFamily: 'var(--ind-mono)', fontSize: 12, color: val ? 'var(--ind-accent)' : 'var(--ind-tx3)', width: 44 }}>
                              {val ? 'ON' : 'OFF'}
                            </span>
                            {s.hint && <span style={{ fontSize: 10, color: 'var(--ind-tx3)' }}>{s.hint}</span>}
                          </>
                        ) : (
                          <>
                            {s.type === 'range' ? (
                              <input type="range" min={s.min} max={s.max} step={s.step}
                                value={val}
                                onChange={e => setTune(k, s.key, parseFloat(e.target.value))}
                                style={{ flex: 1, maxWidth: 320 }} />
                            ) : (
                              <input type="number" min={s.min} max={s.max} step={s.step}
                                value={val}
                                onChange={e => setTune(k, s.key, parseInt(e.target.value, 10) || s.min)}
                                style={{ width: 80, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)' }} />
                            )}
                            <span style={{ fontFamily: 'var(--ind-mono)', fontSize: 12, color: 'var(--ind-accent)', width: 44, textAlign: 'right' }}>
                              {val}
                            </span>
                            {s.key === 'conf' && val <= 0.15 &&
                              <span style={{ fontSize: 10, color: 'var(--ind-crit)' }}>low → false positives</span>}
                            {s.hint && <span style={{ fontSize: 10, color: 'var(--ind-tx3)' }}>{s.hint}</span>}
                          </>
                        )}
                      </div>
                      );
                    })}
                  </div>
                );
              })}
              <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '0 0 8px' }}>
                Higher confidence = fewer false alerts (but may miss weak cases). Persistence = frames the detection
                must hold before alerting. For fire/smoke the <b>temporal</b> toggles reject objects that hold
                still — <i>flame flicker</i> a parked hi-viz vest, <i>plume growth</i> a grey wall — while a real
                flickering or growing fire on a person still alerts. Turn <i>Smoke detection</i> off to run
                fire-only outdoors, where sky/haze trip smoke.
                <br /><br />
                <b>Known limits, measured on site footage.</b> These gates gate <i>movement</i>, not flame, so
                they do <b>not</b> stop a person <i>walking</i> in orange clothing, and they do not stop IR glare
                from a streetlight or porch light at night — both still raise alerts. Animals are handled by the
                model itself. Treat fire/smoke as <b>early warning, not a certified alarm</b>: for Fire NOC or
                insurance compliance you still need VESDA / smoke / heat detectors.
              </p>
            </>
          )}

          {/* face cover: where/when + false-alert monitoring. Needs the module SAVED
              on the camera (health and suggestions read what the box is running). */}
          {enabled.includes('face_cover') && (() => {
            const cam = cameras.find(c => c.idx === scope);
            const live = (cam?.detectors || []).includes('face_cover');
            return (
              <>
                <div className="ind-rail">Face covered — where, when &amp; false-alert monitoring</div>
                <div className="ind-card">
                  {live
                    ? <FaceCoverPanel key={scope} cameraIdx={scope} camera={cam} />
                    : <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: 0 }}>
                        Save to turn face cover on for this camera — its area, hours and monitoring appear here.
                      </p>}
                </div>
              </>
            );
          })()}

        </>
      )}
    </IndustrialLayout>
  );
}
